export enum WebAppRoutes {
  INVENTORY_ITEMS = "inventory-items",
  INVENTORY_POKEMONS = "inventory-pokemons",
  SHOP = "shop",
  CREATE_POKE_TEAM = "create-poke-team",
  LIST_POKE_TEAMS = "list-poke-teams",
  UPDATE_POKE_TEAM = "update-poke-team",
  DELETE_POKE_TEAM = "delete-poke-team",
  PLAYER_BALANCE = "player-balance",
  PLAYER_PROFILE = "player-profile",
  UPDATE_PLAYER_PROFILE = "update-player-profile",
  CREATE_DUEL = "create-duel",
  UPDATE_POKEMON_SKILLS = "update-pokemon-skills",
  UPDATE_POKEMON_NAME = "pokemon/update-name",
  ENQUEUE_PLAYER = "enqueue-player",
  GET_PLAYER_QUEUE_STATUS = "get-player-queue-status",
  DEQUEUE_PLAYER = "dequeue-player",
  QUIT_DUEL = "quit-duel",
  RELEASE_POKEMON = "release-pokemon",
  FUSE_POKEMON = "fuse-pokemon",
  LEADERBOARD = "leaderboard",
  GET_EVOLUTION_OPTIONS = "get-evolution-options",
  EVOLVE_POKEMON = "evolve-pokemon",
  TEAM_SELECTION = "team-selection",
  ONBOARDING = "onboarding",
  MARK_SEEN = "mark-seen",
  // Training routes
  START_TRAINING_BATTLE = "training/start-battle",
  START_SANDBOX_BATTLE = "training/sandbox/start-battle",
  // Box routes
  CREATE_BOX = "box/create",
  UPDATE_BOX = "box/update",
  DELETE_BOX = "box/delete",
  GET_BOXES = "box/list",
  GET_BOX_DETAILS = "box/details",
  MOVE_POKEMONS = "box/move",
  // Dex routes (content-collections-dex — replaces old manual binder)
  DEX_LIST = "dex/list",
  DEX_DETAILS = "dex/details",
  DEX_CLAIM = "dex/claim",
  // Item deck routes (core-item-deck-elixir)
  LIST_ITEM_DECKS = "item-deck/list",
  CREATE_ITEM_DECK = "item-deck/create",
  UPDATE_ITEM_DECK = "item-deck/update",
  DELETE_ITEM_DECK = "item-deck/delete",
  INVENTORY_ITEM_CARDS = "inventory/item-cards",
  INVENTORY_ENERGY = "inventory/energy",
  // Training facility routes (core-training)
  TRAINING_TALENTS_START = "training-talents/start",
  TRAINING_TALENTS_CANCEL = "training-talents/cancel",
  TRAINING_TALENTS_COLLECT = "training-talents/collect",
  TRAINING_TALENTS_RESOLVE = "training-talents/resolve",
  TRAINING_TALENTS_STATUS = "training-talents/status",
  // Pack routes (core-packs)
  LIST_PACKS = "pack/list",
  OPEN_PACK = "pack/open",
  // Pack shop rotation routes (econ-shop-rotation)
  GET_SHOP_ROTATION = "shop-rotation",
  BUY_SHOP_PACK = "buy-shop-pack",
  // Daily key economy routes (econ-keys-daily)
  GET_DAILY_STATE = "daily-state",
  BUY_SIEGE_KEY = "buy-siege-key",
  // Auction routes (econ-auction)
  AUCTION_LIST = "auction/list",
  AUCTION_LOT = "auction/lot",
  AUCTION_BID = "auction/bid",
  // Collection slots routes (econ-collection-slots, E4)
  COLLECTION_EXPAND = "collection/expand",
  COLLECTION_PROMOTE = "collection/promote",
  // Anti-alt routes (hardening-anti-alt, P4)
  SUBMIT_FINGERPRINT = "player/fingerprint",
  ADMIN_BAN_PLAYER = "admin/ban-player",
  // Social layer routes (social-layer, P2)
  GET_FRIENDS = "social/friends",
  FRIEND_REQUEST = "social/friend-request",
  RESPOND_FRIEND_REQUEST = "social/respond-friend-request",
  REMOVE_FRIEND = "social/remove-friend",
  SEND_KEY_GIFT = "social/send-key-gift",
  CLAIM_KEY_GIFT = "social/claim-key-gift",
  GET_LFG_LOBBIES = "social/lfg-lobbies",
  GET_FEED = "social/feed",
  // Siege campaign routes (core-siege C3)
  GET_SIEGE_MAP_CLEARS = "siege/map-clears",
}



// ============================================================================
// ONBOARDING
// ============================================================================

export interface OnboardingInput {
  action:
  | "create_player"
  | "update_country"
  | "pick_starter"
  | "complete"
  | "verify_username"
  | "get_status"
  | "request_alpha_access";
  username?: string | undefined;
  country?: string | undefined;
  starter_type?: string | undefined;
  starter_types?: string[] | undefined;
}

export interface OnboardingOutput {
  ok: boolean;
  message: string;
  available?: boolean;
  alpha_permission?: boolean;
  onboarding_completed?: boolean;
  /** Re-issued when a Player row is created/resolved so the client's JWT carries player_id. */
  access_token?: string;
  player?: {
    id: string;
    username: string;
    country?: string | null;
  };
  starter?: Pick<
    Pokemon,
    "id" | "display_id" | "base_pokemon_name" | "level" | "active_skills"
  >;
  starters?: Array<
    Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "level" | "active_skills"
    >
  >;
}

// ============================================================================
// LEADERBOARD
// ============================================================================

export interface LeaderboardEntry {
  rank: number;
  username: string;
  score: number;
  user_id: string;
  country?: string;
}

export interface LeaderboardOutput {
  season: number;
  remaining_days: number;
  entries: LeaderboardEntry[];
  current_user_entry?: LeaderboardEntry;
}

// ============================================================================
// INVENTORY - ITEMS
// ============================================================================

import {
  PlayerItem,
  Pokemon,
  PokemonChildren,
  PlayerPokemonTeam,
} from "prisma-layer";

export interface InventoryItemsOutput {
  player_items: Pick<PlayerItem, "id" | "item_name" | "amount">[];
}

// ============================================================================
// INVENTORY - POKEMONS
// ============================================================================

export interface InventoryPokemonsOutput {
  player_pokemons: Array<
    Pick<
      Pokemon,
      | "id"
      | "display_id"
      | "name"
      | "base_pokemon_name"
      | "level"
      | "experience"
      | "state"
      | "talents"
      | "known_skills"
      | "active_skills"
      | "created_at"
      | "is_new"
      | "is_shiny"
      | "is_full_art"
      | "box_id"
      | "professor_sessions_used"
    > & {
      pokemon_children: Pick<PokemonChildren, "id">[];
      pokemon_parents: {
        parent: Pick<
          Pokemon,
          "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
        >;
      }[];
      in_training: boolean;
    }
  >;
}

// ============================================================================
// SHOP
// ============================================================================

export interface ShopInput {
  item_name: string;
  amount: number;
}

export interface ShopOutput {
  message: string;
  ok: boolean;
  player_items: Pick<PlayerItem, "id" | "item_name" | "amount">[];
}

// ============================================================================
// PLAYER PROFILE
// ============================================================================

export interface MatchHistory {
  duel_id: string;
  opponent_name: string;
  opponent_id: string;
  result: "win" | "loss" | "draw";
  timestamp: string;
  is_bot_match: boolean;
}

export interface PlayerProfileOutput {
  player_id: string;
  poke_coins: number;
  poke_shards: number;
  country?: string | null;
  username: string;
  alpha_permission: boolean;
  onboarding_completed: boolean;

  // Stats
  stats: {
    elo: number;
    wins: number;
    losses: number;
    duels: number;
    rank: number;
    rewarded_duels_today?: number;
  };

  // Main Team
  main_team: Array<
    Pick<
      Pokemon,
      | "id"
      | "display_id"
      | "base_pokemon_name"
      | "level"
      | "experience"
      | "nature"
      | "talents"
      | "active_skills"
      | "is_shiny"
    >
  >;

  // History
  match_history: MatchHistory[];

  // Meta
  is_own_profile: boolean;
}

export interface UpdatePlayerProfileInput {
  country?: string;
}

export interface UpdatePlayerProfileOutput {
  message: string;
  ok: boolean;
  data: PlayerProfileOutput | null;
}

// ============================================================================
// POKETEAM - CREATE
// ============================================================================

export interface CreateTeamInput {
  name: string;
}

export interface CreateTeamOutput {
  message: string;
  ok: boolean;
  team: Pick<PlayerPokemonTeam, "id" | "name"> | null;
}

// ============================================================================
// POKETEAM - LIST
// ============================================================================

export interface ListTeamsOutput {
  message: string;
  ok: boolean;
  teams: Array<
    Pick<PlayerPokemonTeam, "id" | "name" | "order"> & {
      pokemons: Array<
        Pick<
          Pokemon,
          | "id"
          | "display_id"
          | "base_pokemon_name"
          | "level"
          | "active_skills"
          | "is_shiny"
        >
      >;
      isMainTeam: boolean;
    }
  >;
}

// ============================================================================
// POKETEAM - UPDATE
// ============================================================================

export interface UpdateTeamInput {
  team_id: number;
  name?: string;
  order?: Record<string, number>;
  pokemon_ids?: number[];
  set_main_team?: boolean;
}

export interface UpdateTeamOutput {
  message: string;
  ok: boolean;
  team:
  | (Pick<PlayerPokemonTeam, "id" | "name" | "order"> & {
    pokemons: Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
    >[];
  })
  | null;
}

// ============================================================================
// POKETEAM - DELETE
// ============================================================================

export interface DeleteTeamInput {
  team_id: number;
}

export interface DeleteTeamOutput {
  message: string;
  ok: boolean;
}

// ============================================================================
// PLAYER DATA
// ============================================================================

export interface PlayerBalanceOutput {
  poke_coins: number;
  poke_shards: number;
}

// ============================================================================
// DUEL - CREATE
// ============================================================================

export interface CreateDuelInput {
  opponent_player_id: string;
  duel_draft_type: string;
  is_bot_match: boolean;
}

export interface CreateDuelOutput {
  ok: boolean;
  message: string;
  duel_id?: string;
  websocket_url?: string;
}

// ============================================================================
// POKEMON - UPDATE SKILLS
// ============================================================================

export interface UpdatePokemonSkillsInput {
  pokemon_id: number;
  active_skills: string[];
}

export interface UpdatePokemonSkillsOutput {
  message: string;
  ok: boolean;
  pokemon: Pick<Pokemon, "id" | "display_id" | "active_skills"> | null;
}

// ============================================================================
// POKEMON - UPDATE NAME
// ============================================================================

export interface UpdatePokemonNameInput {
  pokemon_id: number;
  name: string | null;
}

export interface UpdatePokemonNameOutput {
  message: string;
  ok: boolean;
  pokemon: Pick<Pokemon, "id" | "display_id" | "name"> | null;
}

// ============================================================================
// DUEL - QUEUE
// ============================================================================

export interface EnqueuePlayerInput {
  queue_id: string;
  deck_id: number;
  force_bot?: boolean;
}

export interface EnqueuePlayerOutput {
  queue_entry?: {
    queue_id: string;
    status: string;
    joined_at: number;
  };
}

export interface GetPlayerQueueStatusOutput {
  status: "IDLE" | "WAITING" | "MATCHED" | "IN_DUEL";
  queue_entry?: {
    queue_id: string;
    joined_at: number;
    duel_id?: string;
    /** Deck the player queued with — lets the client rebuild the team sprite display after a refresh. */
    deck_id?: number;
  };
}

export interface DequeuePlayerOutput {
  message: string;
  ok: boolean;
}

// ============================================================================
// POKEMON - RELEASE
// ============================================================================

export interface ReleasePokemonInput {
  pokemon_id: number;
}

export interface ReleasePokemonOutput {
  message: string;
  ok: boolean;
  released_pokemon: Pick<
    Pokemon,
    "id" | "display_id" | "base_pokemon_name" | "is_shiny"
  > | null;
  returned_items?: Array<{ item_name: string; amount: number }>;
  /** Coin credited to the player as burn payout (econ-collection-slots, E4). */
  burn_payout?: number;
}

/**
 * Fuse two same-species Pokemon: the invested (kept) mon absorbs the donor's
 * cosmetics (`is_full_art` / `foil`), and the donor is deleted. The kept mon
 * retains all its level/exp/skills and existing relations.
 */
export interface FusePokemonInput {
  /** Invested mon that survives and receives the cosmetics. */
  keep_pokemon_id: number;
  /** Cosmetic donor mon that is consumed. */
  donor_pokemon_id: number;
}

export interface FusePokemonOutput {
  fused_pokemon: Pick<
    Pokemon,
    "id" | "display_id" | "base_pokemon_name" | "is_full_art"
  >;
}

// ============================================================================
// POKEMON - MARK SEEN
// ============================================================================

export interface MarkSeenInput {
  pokemon_id: number;
}

export interface MarkSeenOutput {
  message: string;
  ok: boolean;
}

// ============================================================================
// DUEL - QUIT
// ============================================================================

export interface QuitDuelInput {
  duel_id: string;
}

export interface QuitDuelOutput {
  success: boolean;
  message: string;
}

// ============================================================================
// POKEMON - EVOLUTION
// ============================================================================

export interface EvolutionOptionInfo {
  target_species: string;
  trigger_type: string;
  required_item?: string | null;
  required_level?: number | null;
  is_player_choice?: boolean;
  choice_label?: string;
}

export interface GetEvolutionOptionsInput {
  pokemon_id: number;
}

export interface GetEvolutionOptionsOutput {
  pokemon_id: number;
  pokemon_name: string;
  evolutions: EvolutionOptionInfo[];
  can_evolve: boolean;
}

export interface EvolvePokemonInput {
  pokemon_id: number;
  target_species: string;
}

export interface EvolvePokemonOutput {
  message: string;
  ok: boolean;
  pokemon: Pick<
    Pokemon,
    | "id"
    | "display_id"
    | "base_pokemon_name"
    | "level"
    | "known_skills"
    | "active_skills"
  > | null;
  consumed_item?: string;
}

// ============================================================================
// TEAM - SELECTION (Mini App)
// ============================================================================

export interface TeamSelectionOutput {
  team_id: number;
  team_name: string;
  slots: Array<{
    position: number;
    pokemon:
    | (Pick<
      Pokemon,
      | "id"
      | "display_id"
      | "base_pokemon_name"
      | "name"
      | "level"
      | "active_skills"
      | "is_shiny"
    > & {
      types: string[];
    })
    | null;
    busy: boolean;
    busy_reason?: string;
  }>;
}

// ============================================================================
// DAILY KEY ECONOMY (econ-keys-daily)
// ============================================================================

export interface DailyStateOutput {
  /** Current "siege" PlayerKey balance. */
  siege_keys: number;
  /** Keys bought today + the daily cap. */
  keys_purchased_today: number;
  key_purchase_daily_cap: number;
  /** Coin price of the next buyable key, or null if the daily cap is reached. */
  next_key_price: number | null;
  /** Whether today's guaranteed-pack quest has been claimed. */
  quest_pack_granted: boolean;
  /** Whether today's first-group-win key has been granted. */
  group_win_key_granted: boolean;
  seconds_until_reset: number;
}

export interface BuySiegeKeyOutput {
  ok: boolean;
  message: string;
  siege_keys: number;
  poke_coins: number;
  keys_purchased_today: number;
}

// ============================================================================
// BOX MANAGEMENT
// ============================================================================

export interface PokemonBoxData {
  id: string;
  name: string;
  color: string;
  icon: string;
  pokemon_count?: number;
}

export interface CreateBoxInput {
  name: string;
  color?: string;
  icon?: string;
}

export interface CreateBoxOutput {
  ok: boolean;
  message: string;
  box: PokemonBoxData | null;
}

export interface UpdateBoxInput {
  box_id: string;
  name?: string;
  color?: string;
  icon?: string;
}

export interface UpdateBoxOutput {
  ok: boolean;
  message: string;
  box: PokemonBoxData | null;
}

export interface DeleteBoxInput {
  box_id: string;
}

export interface DeleteBoxOutput {
  ok: boolean;
  message: string;
}

export interface GetBoxesOutput {
  boxes: PokemonBoxData[];
}

export interface GetBoxDetailsInput {
  box_id: string; // "default" for null
}

export interface GetBoxDetailsOutput {
  box: PokemonBoxData | null; // null if default box
  pokemons: Array<
    Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
    > & { gender?: string }
  >;
}

export interface MovePokemonsInput {
  pokemon_ids: number[];
  target_box_id: string | null;
}

export interface MovePokemonsOutput {
  ok: boolean;
  message: string;
}

// ============================================================================
// COLLECTION MANAGEMENT
// ============================================================================

export interface CollectionData {
  id: string;
  display_name: string;
  region?: string;
  total_slots: number;
  obtained_count: number;
}

export interface GetCollectionsOutput {
  collections: CollectionData[];
}

export interface GetCollectionDetailsInput {
  collection_id: string;
}

export interface GetCollectionDetailsOutput {
  collection: {
    id: string;
    display_name: string;
    region?: string;
    slots: string[]; // base_pokemon_names
  };
  obtained_pokemons: Array<{
    base_pokemon_name: string;
    pokemon_id: number;
    display_id: string;
    is_shiny: boolean;
  }>;
  available_species: string[]; // Species the user owns but are not in a collection
}

export interface SetCollectionInput {
  pokemon_id: number;
  collection_id: string | null;
}

export interface SetCollectionOutput {
  ok: boolean;
  message: string;
}

// ============================================================================
// ITEM DECK (core-item-deck-elixir, C1)
// ============================================================================

import { PlayerEnergy, PlayerItemCard, PlayerItemDeck } from "prisma-layer";
import { DeckCardSlot } from "types/item-cards/deck-item-slot";

export type ItemDeckPokemon = Pick<
  Pokemon,
  "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
>;

export interface ItemDeckData {
  id: number;
  name: string;
  card_slots: DeckCardSlot[];
  poke_slots: number[];
  pokemons: ItemDeckPokemon[];
  is_valid_for_match: boolean;
  is_main: boolean;
}

export interface ListItemDecksOutput {
  message: string;
  ok: boolean;
  decks: ItemDeckData[];
}

export interface CreateItemDeckInput {
  name: string;
}

export interface CreateItemDeckOutput {
  message: string;
  ok: boolean;
  deck: Pick<PlayerItemDeck, "id" | "name"> | null;
}

export interface UpdateItemDeckInput {
  deck_id: number;
  name?: string;
  card_slots?: DeckCardSlot[];
  poke_slots?: number[];
  set_main_deck?: boolean;
}

export interface UpdateItemDeckOutput {
  message: string;
  ok: boolean;
  deck: ItemDeckData | null;
}

export interface DeleteItemDeckInput {
  deck_id: number;
}

export interface DeleteItemDeckOutput {
  message: string;
  ok: boolean;
}

export interface InventoryItemCardsOutput {
  player_item_cards: Pick<
    PlayerItemCard,
    "id" | "item_card_key" | "amount" | "level"
  >[];
}

export interface InventoryEnergyOutput {
  player_energy: Pick<PlayerEnergy, "id" | "type" | "amount">[];
}

// ============================================================================
// AUTH - EMAIL/PASSWORD (Cognito)
// ============================================================================

export interface SignupInput {
  email: string;
  password: string;
}

export interface SignupOutput {
  ok: boolean;
  message: string;
  /** True if Cognito requires an emailed confirmation code before login. */
  confirmation_required?: boolean;
}

export interface ConfirmSignupInput {
  email: string;
  code: string;
}

export interface ConfirmSignupOutput {
  ok: boolean;
  message: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginOutput {
  ok: boolean;
  message: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

export interface RefreshInput {
  refresh_token: string;
  /** Cognito refresh requires the original username (email) alongside the token. */
  email: string;
}

export interface RefreshOutput {
  ok: boolean;
  message: string;
  access_token?: string;
  expires_in?: number;
}

export interface ForgotPasswordInput {
  email: string;
}

export interface ForgotPasswordOutput {
  ok: boolean;
  message: string;
}

export interface ConfirmForgotPasswordInput {
  email: string;
  code: string;
  new_password: string;
}

export interface ConfirmForgotPasswordOutput {
  ok: boolean;
  message: string;
}

// ============================================================================
// TRAINING FACILITY (core-training, C4)
// ============================================================================

import { TrainingSession } from "prisma-layer";

export type TrainingOfferData = { slot: number; from: string; to: string };
export type TrainingMutationData = { slot: number; from: string; to: string };

export type TrainingSessionData = Pick<
  TrainingSession,
  | "id"
  | "student_id"
  | "professor_id"
  | "slot_index"
  | "ends_at"
  | "offered_slots"
  | "mutation"
  | "accepted_slot"
  | "energy_cost"
  | "shard_cost"
  | "status"
  | "created_at"
>;

export interface TrainingStartInput {
  student_id: number;
  professor_id: number;
  slot_index: number;
}

export interface TrainingStartOutput {
  session: TrainingSessionData;
}

export interface TrainingCancelInput {
  session_id: number;
}

export interface TrainingCancelOutput {
  session: TrainingSessionData;
}

export interface TrainingCollectInput {
  session_id: number;
}

export interface TrainingCollectOutput {
  session: TrainingSessionData;
  offers: TrainingOfferData[];
  mutation: TrainingMutationData | null;
}

export interface TrainingResolveInput {
  session_id: number;
  accepted_slot: number | null;
}

export interface TrainingResolveOutput {
  session: TrainingSessionData;
  professor_retired: boolean;
}

export interface TrainingSlotData {
  slot_index: number;
  state: "free" | "on_cooldown" | "running" | "ready" | "offered";
  cooldown_until: string | null;
  session: TrainingSessionData | null;
}

export interface TrainingStatusOutput {
  slots: TrainingSlotData[];
}

// ============================================================================
// PACKS (core-packs, C2)
// ============================================================================

import { PokemonType } from "prisma-layer";
import { FoilConfig } from "types/cards/foil";

export interface PlayerPackData {
  id: number;
  pack_definition_key: string;
  created_at: string;
}

export interface ListPacksOutput {
  packs: PlayerPackData[];
}

export interface OpenPackInput {
  pack_instance_id: number;
}

export interface PackRevealPokemon {
  id: number;
  display_id: string;
  base_pokemon_name: string;
  is_shiny: boolean;
  is_full_art: boolean;
  foil: FoilConfig | null;
  talents: PokemonType[];
  /** 0-9 talent purity — the reveal's "potential" signal. */
  purity: number;
}

export interface PackEnergyGrant {
  type: PokemonType;
  amount: number;
}

export interface PackItemCardGrant {
  item_card_key: string;
  amount: number;
}

export interface OpenPackOutput {
  pack_definition_key: string;
  /** null for ENERGY_ONLY packs. */
  pokemon: PackRevealPokemon | null;
  energy: PackEnergyGrant[];
  item_cards: PackItemCardGrant[];
}

// ============================================================================
// PACK SHOP ROTATION (econ-shop-rotation, E3)
// ============================================================================

export interface ShopOfferData {
  pack_definition_key: string;
  price: number;
  cap: number;
  purchased_today: number;
}

export interface GetShopRotationOutput {
  offers: ShopOfferData[];
  seconds_until_reset: number;
}

export interface BuyShopPackInput {
  pack_definition_key: string;
}

export interface BuyShopPackOutput {
  message: string;
  ok: boolean;
  pack_definition_key: string;
  purchased_today: number;
  poke_coins: number;
}

// ============================================================================
// AUCTION (econ-auction, E2)
// ============================================================================

export type AuctionLotStatus = "OPEN" | "SOLD" | "EXPIRED";
export type AuctionEscrowStatus = "LEADING" | "OUTBID" | "WON" | "REFUNDED";

/** Requester's own escrow state on a lot, surfaced so the board can show "you're leading / outbid". */
export interface AuctionMyEscrow {
  max_amount: number;
  locked_amount: number;
  status: AuctionEscrowStatus;
}

export interface AuctionLotSummary {
  id: string;
  pack_definition_key: string;
  /** Pack "type only" preview (dec2 §2): pack display name + tier, no odds. */
  pack_name: string;
  pack_tier: string | null;
  kind: "DAILY" | "WEEKLY";
  current_price: number;
  reserve_price: number;
  /** Minimum acceptable next bid, precomputed for the client. */
  min_next_bid: number;
  status: AuctionLotStatus;
  closes_at: string;
  my_escrow: AuctionMyEscrow | null;
}

export interface AuctionListOutput {
  lots: AuctionLotSummary[];
}

export interface AuctionBidHistoryEntry {
  player_id: string;
  display_name: string;
  amount: number;
  created_at: string;
}

export interface AuctionLotDetailInput {
  lot_id: string;
}

export interface AuctionLotDetailOutput {
  lot: AuctionLotSummary;
  bids: AuctionBidHistoryEntry[];
  /** final_price of the last N SOLD lots with the same pack_definition_key. */
  recent_closing_prices: number[];
}

export interface AuctionBidInput {
  lot_id: string;
  max_amount: number;
}

export interface AuctionBidOutput {
  ok: boolean;
  message: string;
  lot: AuctionLotSummary | null;
  poke_coins: number;
}

// ============================================================================
// COLLECTION SLOTS (econ-collection-slots, E4)
// ============================================================================

export interface CollectionExpandOutput {
  ok: boolean;
  message: string;
  /** New total housing cap after expansion. */
  new_cap: number;
  /** Player's remaining coin balance. */
  poke_coins: number;
  /** Price of the next expansion (null if at MAX_EXPANSIONS). */
  next_expansion_price: number | null;
  /** How many more expansion purchases remain. */
  expansions_remaining: number;
}

export interface CollectionPromoteInput {
  pokemon_id: number;
}

export interface CollectionPromoteOutput {
  ok: boolean;
  message: string;
  pokemon_id: number;
  /** Updated in-collection count. */
  used: number;
  /** Current cap. */
  cap: number;
}

// ============================================================================
// DEX (content-collections-dex, P6)
// ============================================================================

import { DexReward } from "game/data/collections";

export interface DexSetSummary {
  id: string;
  display_name: string;
  region?: string;
  total: number;
  obtained_count: number;
  claimable: boolean;
  claimed: boolean;
  reward: DexReward;
}

export interface DexListOutput {
  total_species: number;
  sets: DexSetSummary[];
}

export interface DexSlot {
  species_name: string;
  status: "obtained" | "reachable" | "unknown";
  shiny_seen?: boolean;
  reachable_from?: string[];
}

export interface DexDetailsOutput {
  set_id: string;
  display_name: string;
  obtained_count: number;
  total: number;
  claimable: boolean;
  claimed: boolean;
  reward: DexReward;
  slots: DexSlot[];
}

export interface DexClaimInput {
  set_id: string;
}

export interface DexClaimOutput {
  ok: boolean;
  set_id: string;
  reward: DexReward;
}

// ============================================================================
// SOCIAL LAYER (social-layer, P2)
// ============================================================================

export type FriendshipStatus = "PENDING" | "ACCEPTED";
export type FeedEventType =
  | "SHINY_PACK_REVEAL"
  | "FULL_ART_PACK_REVEAL"
  | "AUCTION_LOT_SOLD"
  | "SIEGE_PERSONAL_BEST";

export interface FriendEntry {
  friendship_id: number;
  player_id: string;
  username: string;
  country?: string | null;
  status: FriendshipStatus;
  /** Whether I sent the request (false = incoming). Only set for PENDING rows. */
  is_outgoing?: boolean;
  /** True if this friend is eligible to receive a key gift from me today. */
  giftable_today: boolean;
  /** True if there's an unclaimed gift already pending from me to them (no double-send). */
  gift_pending: boolean;
}

export interface PendingGiftEntry {
  gift_id: number;
  sender_id: string;
  sender_username: string;
  expires_at: string;
}

export interface GetFriendsOutput {
  friends: FriendEntry[];
  pending_received_gifts: PendingGiftEntry[];
}

export interface FriendRequestInput {
  player_id: string;
}

export interface FriendRequestOutput {
  ok: boolean;
  message: string;
}

export type FriendRequestResponse = "accept" | "decline";

export interface RespondFriendRequestInput {
  friendship_id: number;
  response: FriendRequestResponse;
}

export interface RespondFriendRequestOutput {
  ok: boolean;
  message: string;
}

export interface RemoveFriendInput {
  friendship_id: number;
}

export interface RemoveFriendOutput {
  ok: boolean;
  message: string;
}

export interface SendKeyGiftInput {
  friend_id: string;
}

export interface SendKeyGiftOutput {
  ok: boolean;
  message: string;
}

export interface ClaimKeyGiftInput {
  gift_id: number;
}

export interface ClaimKeyGiftOutput {
  ok: boolean;
  message: string;
  siege_keys: number;
}

export interface LfgLobbyEntry {
  siege_run_id: string;
  invite_code: string;
  host_username: string;
  tier: number | null;
  map_key: string | null;
  open_slots: number;
  total_slots: number;
}

export interface GetLfgLobbiesOutput {
  lobbies: LfgLobbyEntry[];
  total: number;
}

export interface FeedEventEntry {
  id: number;
  type: FeedEventType;
  player_id: string;
  username: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface GetFeedOutput {
  events: FeedEventEntry[];
}
